/**
 * useAds — BridgeAPI-only ads hook (MySQL, no Supabase)
 */

import { useQuery } from "@tanstack/react-query";
import { bridgeApi } from "@/lib/bridgeApi";

export interface Ad {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  ad_type: string;
  placement: string;
  target_type: string | null;
  target_categories: number[] | null;
  priority: number;
  is_active: boolean;
}

function mapRow(r: any): Ad {
  const target_categories = Array.isArray(r.target_categories)
    ? r.target_categories
    : (() => {
        if (!r.target_categories) return null;
        try {
          const parsed = JSON.parse(r.target_categories);
          return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
        } catch {
          return null;
        }
      })();

  return {
    id: String(r.id),
    title: r.title || "Sponsored",
    description: r.description ?? null,
    image_url: r.image_url ?? null,
    link_url: r.link_url ?? null,
    ad_type: r.ad_type || "banner",
    placement: r.placement,
    target_type: r.target_type || "all",
    target_categories,
    priority: r.priority != null ? Number(r.priority) : 0,
    is_active: !!Number(r.is_active),
  };
}
export function useAds(placement: string, categoryType?: number) {
  return useQuery<Ad[]>({
    queryKey: ['ads', placement, categoryType],
    queryFn: async (): Promise<Ad[]> => {
      try {
        const params: Record<string, string> = {
          placement,
          is_active: '1',
        };
        if (categoryType) {
          params.category_type = String(categoryType);
        }

        const result = await bridgeApi.call<any>('ads_public_list', params);
        const rows: any[] = result?.data ?? result ?? [];

        if (!Array.isArray(rows) || rows.length === 0) {
          return [];
        }

        const ads = rows.map(mapRow).filter(ad => ad.is_active);

        return categoryType
          ? ads.filter(ad => !ad.target_categories || ad.target_categories.length === 0 || ad.target_categories.includes(categoryType))
          : ads;

      } catch (error) {
        console.error('[useAds] Failed to load ads:', error);
        return [];
      }
    },
    // Short staleTime so a newly-approved ad shows up on the next page visit
    // within this SPA session without needing a hard refresh — ads don't
    // change often enough to need aggressive caching, but "admin approved it
    // and it should show up soon" is a real expectation worth honoring.
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Non-blocking impression/click tracking
export async function trackAdImpression(adId: string): Promise<void> {
  try {
    await bridgeApi.call('ads_track_impression', { ad_id: adId, page_url: window.location.href });
  } catch { /* silent */ }
}

export async function trackAdClick(adId: string): Promise<void> {
  try {
    await bridgeApi.call('ads_track_click', { ad_id: adId, referrer: document.referrer });
  } catch { /* silent */ }
}